import { ProjectFields, YesObject } from '@yes.technology/react-toolkit'

const REGEX_VALUE_PATTERN = /(\{\{[^\}]+\}\})/g
const OBJECT_KEY_PATTERN = /[{}]/g

type ConcatenateArgs = {
  sourceMap: string
  object: YesObject
}

export const getObjectKey = (object: string) =>
  object.replace(OBJECT_KEY_PATTERN, '').split('.')

export const getProjectFields = (
  sourceMap: string
): ProjectFields | undefined => {
  const params = sourceMap.match(REGEX_VALUE_PATTERN)
  return params?.map(getObjectKey).reduce((accumulator, keys) => {
    const key = keys[0]
    return { ...accumulator, [key]: 1 }
  }, {})
}

const getValueFromObject = (object: YesObject, keys: string[]) => {
  let value = undefined

  for (const key of keys) {
    if (typeof object?.[key] === 'string') {
      value = object[key]
    } else {
      return undefined
    }
  }

  return value || ''
}

export const concatenate = ({ sourceMap, object }: ConcatenateArgs) => {
  const params = sourceMap.match(REGEX_VALUE_PATTERN)

  if (!params) {
    return sourceMap
  }

  return params.reduce((accumulator, param) => {
    const objectKey = getObjectKey(param)
    const value = getValueFromObject(object, objectKey)

    if (value && typeof value === 'string') {
      return accumulator.replace(param, value)
    }

    return accumulator
  }, sourceMap)
}

export const matchesSourceMap = (sourceMap: string, value: string) => {
  if (typeof sourceMap !== 'string' || typeof value !== 'string') return false

  const tokenRegex = /(\{\{[^\}]+\}\})/g

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  let lastIndex = 0
  const parts: string[] = []
  let match: RegExpExecArray | null

  while ((match = tokenRegex.exec(sourceMap)) !== null) {
    const index = match.index
    const before = sourceMap.slice(lastIndex, index)
    if (before) parts.push(escapeRegExp(before))

    parts.push('([\\s\\S]+?)')

    lastIndex = index + match[0].length
  }

  const tail = sourceMap.slice(lastIndex)
  if (tail) parts.push(escapeRegExp(tail))

  const pattern = `^${parts.join('')}$`
  const re = new RegExp(pattern)

  return re.test(value)
}
